import React, { useState, useEffect, useContext, useRef } from "react"
import {
  header,
  burgerMenu,
  burgerMenuOpen,
  logoSite,
  headerSearch,
  searchButton,
  searchButtonActive,
  notificationButton,
  burgerMenuTitle,
  logoSiteImg,
  skipLink,
} from "./header.module.scss"
import { BurgerSpan, SearchButton, LogoSite } from "./header-styled"
import ImageFromDrupal from "gatsby-theme-blank/src/components/Image/image-from-drupal"
import PropTypes from "prop-types"
import Icon from "gatsby-theme-blank/src/components/Icon/icon"
import { useBuildData } from "gatsby-theme-blank/src/hooks/use-build-data"
import AniLink from "gatsby-plugin-transition-link/AniLink"
import { useHeader } from "gatsby-theme-blank/src/components/Layout/Header/header-query"
import { NotificationContext } from "gatsby-theme-blank/src/components/notification-context"
import MainMenu from "gatsby-theme-blank/src/components/Layout/Header/MainMenu/main-menu"
import MainSearch from "./MainSearch/main-search"
import EditButton from "gatsby-theme-blank/src/components/EditButton/edit-button"

//-----------------------Parent component---------------------------//

const Header = ({ children, isGdd, isIndex }) => {
  const notifButton = useRef(null)
  const buildColors = useBuildData().theme_settings.palette
  const buildInfos = useBuildData().site_settings
  const data = useHeader()
  const [openBurger, setOpenBurger] = useState(false)
  const [openSearch, setOpenSearch] = useState(false)
  const { setIsOpen } = useContext(NotificationContext)
  const menuData = data.allMenuLinkContentMenuLinkContent.edges
  const [localNavigator, setLocalNavigator] = useState(null)
  const burgerMenuRef = useRef()

  useEffect(() => {
    setLocalNavigator(navigator)
  }, [])

  useEffect(() => {
    if (typeof document !== `undefined`) {
      const container = document.getElementById("menuItems")
      const handleFocusOut = (event) => {
        const isInside =
          container.contains(event.relatedTarget) ||
          burgerMenuRef.current === event.relatedTarget
        if (!isInside && openBurger) {
          burgerMenuRef.current.focus()
        }
      }

      document.addEventListener("focusout", handleFocusOut, true)
      return () => {
        document.removeEventListener("focusout", handleFocusOut, true)
      }
    }
  }, [openBurger])
  if (typeof document !== `undefined`) {
    document.addEventListener("keyup", (event) => {
      if (event.key === "Escape") {
        setOpenBurger(false)
      }
    })
  }
  const handleNotifOnFocus = () => {
    if (notifButton.current) {
      notifButton.current.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          setIsOpen(true)
        }
      })
    }
  }

  const handleSearchButtonKeyDown = (event) => {
    if (event.key === "Enter") {
      setOpenSearch(!openSearch)
      const searchInput = document.getElementById("search-input")
      if (searchInput !== null) {
        searchInput.focus()
      }
    }
  }

  return (
    <>
      <a
        className={skipLink}
        href={"#top"}
        aria-label={"Lien d'accès rapide au menu"}
        tabIndex="0"
      >
        {"Lien d'accès rapide au menu"}
      </a>
      <a
        className={skipLink}
        href={"#main"}
        aria-label={"Lien d'accès rapide au contenu principal"}
        tabIndex="0"
      >
        {"Lien d'accès rapide au contenu principal"}
      </a>
      <a
        className={skipLink}
        href={"#search-button"}
        aria-label={"Lien d'accès rapide à la recherche"}
        tabIndex="0"
      >
        {"Lien d'accès rapide à la recherche"}
      </a>
      <a
        className={skipLink}
        href={"#footer"}
        aria-label={"Lien d'accès rapide au pied de page"}
        tabIndex="0"
      >
        {"Lien d'accès rapide au pied de page"}
      </a>
      {!isGdd && (
        <header className={header} role="banner">
          <LogoSite className={logoSite}>
            <EditButton link={"/admin/site-configuration"} />

            <AniLink
              aria-label={buildInfos.website_name + " - Accueil"}
              paintDrip
              hex={buildColors.a_color_gradients.a_dark}
              to="/"
            >
              {buildInfos.website_logo.fid === null ? (
                <Icon size={48} icon="home" />
              ) : (
                <ImageFromDrupal
                  className={logoSiteImg}
                  alt={"Logo " + buildInfos.website_name}
                  drupal_fid={buildInfos.website_logo.fid}
                />
              )}
            </AniLink>
          </LogoSite>

          <nav
            ref={burgerMenuRef}
            title={!openBurger ? "Ouverture du menu" : "Fermeture du menu"}
            aria-expanded={openBurger}
            tabIndex={0}
            role="button"
            className={!openBurger ? burgerMenu : burgerMenuOpen}
            onClick={() => setOpenBurger(!openBurger)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                setOpenBurger(!openBurger)
              }
            }}
          >
            <BurgerSpan></BurgerSpan>
            <BurgerSpan></BurgerSpan>
            <BurgerSpan></BurgerSpan>
            <BurgerSpan></BurgerSpan>
          </nav>
          <p className={burgerMenuTitle}>Menu</p>

          <MainMenu colors={buildColors} mainMenuData={menuData} />
          <div className={headerSearch}>
            {buildInfos.webpush_enabled &&
            localNavigator?.serviceWorker &&
            isIndex ? (
              <button
                ref={notifButton}
                aria-label="Gestion des notifications"
                className={notificationButton}
                onClick={() => setIsOpen(true)}
                tabIndex="0"
                onFocus={handleNotifOnFocus}
              >
                <Icon icon="bell" size={22}></Icon>
              </button>
            ) : null}
            <SearchButton
              role="button"
              id={"search-button"}
              aria-label="Recherche"
              tabIndex={0}
              className={!openSearch ? searchButton : searchButtonActive}
              activeSearch={searchButtonActive}
              onClick={() => setOpenSearch(!openSearch)}
              aria-expanded={openSearch}
              onKeyDown={handleSearchButtonKeyDown}
            >
              <Icon icon="search" size={22}></Icon>
            </SearchButton>
          </div>
        </header>
      )}
      <MainSearch isIndex={isIndex} open={openSearch}>
        {children}
      </MainSearch>
    </>
  )
}
//-----------------------Parent component---------------------------//

//-----------------------Prop-Types---------------------------//

Header.propTypes = {
  children: PropTypes.node.isRequired,
  isGdd: PropTypes.bool,
  isIndex: PropTypes.bool,
}

//-----------------------Prop-Types---------------------------//

export default Header
